import React, { useContext, useEffect, useRef, useState } from "react";
import { registration, registrationCodeRust } from "../http/userAPI";
import { useNavigate } from "react-router-dom";
import { HOME_ROUTE } from "../utils/consts";
import { Context } from "..";
import { observer } from "mobx-react-lite";
import {getApiErrorMessage} from "../http/apiError";
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    Divider
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CSSTransition } from "react-transition-group";
import * as THREE from "three";
import "./Login.css";

const Registration = observer(() => {
    const { user } = useContext(Context);
    const navigate = useNavigate();

    const [authMode, setAuthMode] = useState("default"); // default | rust
    const [rustCode, setRustCode] = useState("");

    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [passwordError, setPasswordError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showForm, setShowForm] = useState(false);

    const formRef = useRef(null);
    const canvasRef = useRef(null);

    /* ================= VALIDATION ================= */

    const validatePassword = (value) => {
        const regex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return regex.test(value)
            ? ""
            : "Пароль должен быть не менее 8 символов и содержать цифру, заглавную и строчную буквы и спецсимвол.";
    };

    const validateEmail = (value) => {
        const regex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
        return regex.test(value) ? "" : "Введите корректный email.";
    };

    /* ================= HANDLERS ================= */

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        setPasswordError(validatePassword(value));
    };

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmPassword(value);
        setConfirmPasswordError(
            value !== password ? "Пароли не совпадают." : ""
        );
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        setEmailError(validateEmail(value));
    };

    /* ================= ACTIONS ================= */

    const registerDefault = async () => {
        if (passwordError || confirmPasswordError || emailError) return;

        try {
            const data = await registration(username, email, password);
            user.setUser(data);
            navigate(HOME_ROUTE);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Ошибка регистрации'));
        }
    };

    const registerRust = async () => {
        try {
            const data = await registrationCodeRust(rustCode);
            user.setUser(data);
            navigate(HOME_ROUTE);
        } catch (e) {
            console.error(getApiErrorMessage(e, 'Ошибка регистрации'));
        }
    };

    const goBack = () => {
        setShowForm(false);
        setTimeout(() => navigate(-1), 300);
    };

    /* ================= UI EFFECTS ================= */

    useEffect(() => {
        setTimeout(() => setShowForm(true), 50);
    }, []);

    useEffect(() => {
        const resize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    useEffect(() => {
        const outside = (e) => {
            if (!isMobile && formRef.current && !formRef.current.contains(e.target)) {
                setShowForm(false);
                setTimeout(() => navigate(-1), 300);
            }
        };
        document.addEventListener("mousedown", outside);
        return () => document.removeEventListener("mousedown", outside);
    }, [isMobile, navigate]);

    /* ================= 3D BACKGROUND ================= */

    useEffect(() => {
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true
        });

        renderer.setSize(window.innerWidth, window.innerHeight);
        camera.position.z = 5;

        const geometry = new THREE.BufferGeometry();
        const vertices = [];

        for (let i = 0; i < 20; i++) {
            vertices.push(
                THREE.MathUtils.randFloatSpread(200),
                THREE.MathUtils.randFloatSpread(2),
                THREE.MathUtils.randFloatSpread(200)
            );
        }

        geometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(vertices, 3)
        );

        const material = new THREE.PointsMaterial({
            size: 0.7,
            transparent: true,
            opacity: 0.7
        });

        const stars = new THREE.Points(geometry, material);
        scene.add(stars);

        const animate = () => {
            stars.rotation.x += 0.0005;
            stars.rotation.y += 0.0005;
            renderer.render(scene, camera);
            requestAnimationFrame(animate);
        };

        animate();

        return () => renderer.dispose();
    }, []);

    /* ================= RENDER ================= */

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isMobile ? "background.default" : "rgba(0,0,0,1)",
                zIndex: 1300
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1
                }}
            />

            <CSSTransition in={showForm} timeout={300} classNames="fade" unmountOnExit>
                <Box
                    ref={formRef}
                    sx={{
                        width: isMobile ? "100%" : 420,
                        bgcolor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        boxShadow: isMobile ? "none" : 6,
                        position: "relative",
                        zIndex: 2
                    }}
                >
                    <IconButton
                        onClick={goBack}
                        sx={{ position: "absolute", top: 20, right: 20 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Typography variant="h5" gutterBottom>
                        {authMode === "default"
                            ? "Регистрация"
                            : "Регистрация через Rust"}
                    </Typography>

                    {authMode === "default" && (
                        <>
                            <TextField
                                fullWidth
                                label="Логин"
                                margin="normal"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="new-password"
                            />

                            <TextField
                                fullWidth
                                label="Email"
                                margin="normal"
                                value={email}
                                onChange={handleEmailChange}
                                error={!!emailError}
                                helperText={emailError}
                                autoComplete="new-password"
                            />

                            <TextField
                                fullWidth
                                label="Пароль"
                                type="password"
                                margin="normal"
                                value={password}
                                onChange={handlePasswordChange}
                                error={!!passwordError}
                                helperText={passwordError}
                                autoComplete="new-password"
                            />

                            <TextField
                                fullWidth
                                label="Подтвердите пароль"
                                type="password"
                                margin="normal"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                error={!!confirmPasswordError}
                                helperText={confirmPasswordError}
                                autoComplete="new-password"
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={registerDefault}
                                disabled={
                                    !!passwordError ||
                                    !!confirmPasswordError ||
                                    !!emailError ||
                                    !username ||
                                    !email ||
                                    !password ||
                                    !confirmPassword
                                }
                            >
                                Зарегистрироваться
                            </Button>

                            <Divider sx={{ my: 2 }}>или</Divider>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setAuthMode("rust")}
                            >
                                Регистрация через Rust
                            </Button>
                        </>
                    )}

                    {authMode === "rust" && (
                        <>
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: "background.default",
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Авторизация через игру Rust
                                </Typography>
                                <Typography variant="body2">
                                    Введите команду <b>/login</b> в игре и укажите
                                    полученный код ниже (действителен 30 минут)
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="Код из игры"
                                margin="normal"
                                value={rustCode}
                                onChange={(e) =>
                                    setRustCode(e.target.value.replace(/\D/g, ""))
                                }
                                autoComplete="new-password"
                                inputProps={{
                                    maxLength: 6,
                                    inputMode: "numeric"
                                }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={registerRust}
                                disabled={rustCode.length !== 6}
                            >
                                Подтвердить и зарегистрироваться
                            </Button>

                            <Button
                                fullWidth
                                variant="text"
                                sx={{ mt: 1 }}
                                onClick={() => setAuthMode("default")}
                            >
                                Назад к обычной регистрации
                            </Button>
                        </>
                    )}
                </Box>
            </CSSTransition>
        </Box>
    );
});

export default Registration;
